// Image Generation Types

export type ImageProvider = 'openai' | 'stability' | 'leonardo' | 'gemini';

export interface ImageGenerationRequest {
  prompt: string;
  provider: ImageProvider;
  model?: string;
  width?: number;
  height?: number;
  negativePrompt?: string;
  styleReferenceIds?: number[];
}

export interface GeneratedImage {
  id: string;
  url: string;
  prompt: string;
  provider: ImageProvider;
  width: number;
  height: number;
  revisedPrompt?: string;
  createdAt: string;
}

export interface StyleReference {
  id: string;
  name: string;
  description?: string;
  imageUrl: string;
  thumbnailUrl: string;
  category: 'style' | 'character' | 'branding' | 'scene';
  tags: string[];
  embedding?: number[];
  createdAt: string;
}

export interface CoverDesign {
  id: string;
  title: string;
  subtitle?: string;
  authorName: string;
  imageUrl: string;
  layout: 'centered' | 'top' | 'bottom' | 'full-bleed';
  titleFont: string;
  titleColor: string;
  subtitleColor?: string;
  authorColor?: string;
  backgroundColor: string;
  overlayOpacity?: number;
}

export interface ImageBlock {
  id: string;
  sectionId: number;
  position: 'before' | 'after' | 'inline';
  imageUrl: string;
  caption?: string;
  width: 'small' | 'medium' | 'large' | 'full';
  alignment: 'left' | 'center' | 'right';
  generatedPrompt?: string;
  provider?: ImageProvider;
}

export interface PublishConfig {
  format: 'epub' | 'kindle' | 'pdf';
  includeImages: boolean;
  includeCover: boolean;
  imageQuality: 'web' | 'print';
  metadata: PublishMetadata;
}

export interface PublishMetadata {
  title: string;
  author: string;
  description?: string;
  isbn?: string;
  language: string;
  publisher?: string;
  publishDate?: string;
  genre?: string;
  keywords?: string[];
}

export interface ImageProviderConfig {
  name: string;
  needsApiKey: boolean;
  supportedSizes: { width: number; height: number; label: string }[];
  supportsNegativePrompt: boolean;
  maxPromptLength: number;
}

// Available models for each image provider
export const IMAGE_MODELS: Record<ImageProvider, { id: string; name: string }[]> = {
  openai: [
    { id: 'dall-e-3', name: 'DALL-E 3' },
    { id: 'dall-e-2', name: 'DALL-E 2' },
  ],
  stability: [
    { id: 'stable-diffusion-xl-1024-v1-0', name: 'SDXL 1.0' },
    { id: 'stable-diffusion-v1-6', name: 'SD 1.6' },
    { id: 'stable-diffusion-xl-beta-v2-2-2', name: 'SDXL Beta' },
    { id: 'stable-core', name: 'Stable Core' },
    { id: 'sd3', name: 'SD3' },
    { id: 'sd3-turbo', name: 'SD3 Turbo' },
  ],
  leonardo: [
    { id: 'ac614f96-1082-45bf-be9d-757f2d31c174', name: 'Leonardo Diffusion XL' },
    { id: '1e60896f-3c26-4296-8ecc-53e2afecc132', name: 'Leonardo Lightning XL' },
    { id: 'b24e16ff-06e3-43eb-8d33-4416c2d75876', name: 'Leonardo Kino XL' },
    { id: '5c232a9e-9061-4777-980a-ddc8e65647c6', name: 'Leonardo Vision XL' },
    { id: 'aa77f04e-3eec-4034-9c07-d0f619684628', name: 'Leonardo Anime XL' },
    { id: '2067ae52-33fd-4a82-bb92-c2c55e7d2786', name: 'AlbedoBase XL' },
    { id: 'b63f7119-31dc-4540-969b-2a9df997e173', name: 'DreamShaper v7' },
    { id: 'e316348f-7773-490e-adcd-46757c738eb7', name: 'Absolute Reality v1.6' },
  ],
  gemini: [
    { id: 'imagen-3.0-generate-001', name: 'Imagen 3.0' },
    { id: 'imagen-3.0-fast-generate-001', name: 'Imagen 3.0 Fast' },
  ],
};

export const IMAGE_PROVIDERS: Record<ImageProvider, ImageProviderConfig> = {
  openai: {
    name: 'OpenAI DALL-E',
    needsApiKey: true,
    supportedSizes: [
      { width: 1024, height: 1024, label: 'Square (1024x1024)' },
      { width: 1792, height: 1024, label: 'Landscape (1792x1024)' },
      { width: 1024, height: 1792, label: 'Portrait (1024x1792)' },
    ],
    supportsNegativePrompt: false,
    maxPromptLength: 4000,
  },
  stability: {
    name: 'Stability AI',
    needsApiKey: true,
    supportedSizes: [
      { width: 1024, height: 1024, label: 'Square (1024x1024)' },
      { width: 1152, height: 896, label: 'Landscape (1152x896)' },
      { width: 896, height: 1152, label: 'Portrait (896x1152)' },
      { width: 1344, height: 768, label: 'Wide (1344x768)' },
      { width: 768, height: 1344, label: 'Tall (768x1344)' },
    ],
    supportsNegativePrompt: true,
    maxPromptLength: 2000,
  },
  leonardo: {
    name: 'Leonardo AI',
    needsApiKey: true,
    supportedSizes: [
      { width: 1024, height: 1024, label: 'Square (1024x1024)' },
      { width: 1360, height: 768, label: 'Landscape (1360x768)' },
      { width: 768, height: 1360, label: 'Portrait (768x1360)' },
      { width: 1536, height: 640, label: 'Cinematic (1536x640)' },
    ],
    supportsNegativePrompt: true,
    maxPromptLength: 1000,
  },
  gemini: {
    name: 'Google Gemini',
    needsApiKey: true,
    supportedSizes: [
      { width: 1024, height: 1024, label: 'Square (1:1)' },
      { width: 1280, height: 768, label: 'Landscape (16:9)' },
      { width: 768, height: 1280, label: 'Portrait (9:16)' },
    ],
    supportsNegativePrompt: false,
    maxPromptLength: 5000,
  },
};

export interface StyleReferenceUpload {
  file: File;
  name: string;
  description?: string;
  category: StyleReference['category'];
  tags: string[];
}

export interface CoverTemplate {
  id: string;
  name: string;
  preview: string;
  layout: CoverDesign['layout'];
  defaultTitleFont: string;
  defaultColors: {
    title: string;
    subtitle: string;
    author: string;
    background: string;
  };
}

export const COVER_TEMPLATES: CoverTemplate[] = [
  {
    id: 'classic-centered',
    name: 'Classic Centered',
    preview: '/templates/cover-classic.png',
    layout: 'centered',
    defaultTitleFont: 'Georgia',
    defaultColors: {
      title: '#FFFFFF',
      subtitle: '#E0E0E0',
      author: '#FFFFFF',
      background: '#1a1a2e',
    },
  },
  {
    id: 'modern-top',
    name: 'Modern Top',
    preview: '/templates/cover-modern.png',
    layout: 'top',
    defaultTitleFont: 'Helvetica',
    defaultColors: {
      title: '#000000',
      subtitle: '#333333',
      author: '#666666',
      background: '#FFFFFF',
    },
  },
  {
    id: 'bold-bottom',
    name: 'Bold Bottom',
    preview: '/templates/cover-bold.png',
    layout: 'bottom',
    defaultTitleFont: 'Impact',
    defaultColors: {
      title: '#FFD700',
      subtitle: '#FFFFFF',
      author: '#CCCCCC',
      background: '#000000',
    },
  },
  {
    id: 'full-bleed',
    name: 'Full Bleed',
    preview: '/templates/cover-fullbleed.png',
    layout: 'full-bleed',
    defaultTitleFont: 'Arial',
    defaultColors: {
      title: '#FFFFFF',
      subtitle: '#F0F0F0',
      author: '#FFFFFF',
      background: 'transparent',
    },
  },
];
